export type CampaignEntryWeightMode = 'flat' | 'weighted';

export type CampaignPrizeType = 'digital_asset' | 'voucher' | 'other';

export type CampaignCardPreference = 'visa' | 'unionpay';

export interface CampaignTradeQualificationRules {
  enabled?: boolean;
  eligibleMarkets?: string[];
  minTradeValueUsdc?: number;
  requiredForDraw?: boolean;
  pendingReminderEmailsEnabled?: boolean;
}

export interface CampaignCardNetworkPreferenceRules {
  enabled?: boolean;
  required?: boolean;
  options?: CampaignCardPreference[];
}

export interface CampaignJoinPreferencesRules {
  cardNetwork?: CampaignCardNetworkPreferenceRules;
}

export interface CampaignLegalDocumentsRules {
  termsAndConditions?: string;
  privacyPolicy?: string;
  termsRequireAcknowledgement?: boolean;
  privacyRequireAcknowledgement?: boolean;
  /** @deprecated Use per-document flags. Kept for backward compatibility. */
  requireAcknowledgement?: boolean;
  contentFormat?: 'markdown' | 'plain';
}

export interface ParsedCampaignRules {
  baseWeight?: number;
  entryWeightMode?: CampaignEntryWeightMode;
  newUserMultiplier?: number;
  newUserDaysThreshold?: number;
  showNewUserBonusBanner?: boolean;
  tradeQualification?: CampaignTradeQualificationRules;
  joinPreferences?: CampaignJoinPreferencesRules;
  legal?: CampaignLegalDocumentsRules;
  /** Maps tier_order → prize type for public UI when gRPC omits prize_type. */
  prizeTypesByTierOrder?: Partial<Record<number, CampaignPrizeType>>;
}

export function parseRulesConfig(rulesConfig: string): ParsedCampaignRules {
  try {
    const parsed = JSON.parse(rulesConfig || '{}');
    const tradeQualification = parsed.trade_qualification;
    const joinPreferences = parsed.join_preferences;
    const legal = parsed.legal;

    return {
      baseWeight: typeof parsed.base_weight === 'number' ? parsed.base_weight : undefined,
      entryWeightMode:
        parsed.entry_weight_mode === 'flat' || parsed.entry_weight_mode === 'weighted'
          ? parsed.entry_weight_mode
          : undefined,
      newUserMultiplier:
        typeof parsed.new_user_weight_multiplier === 'number' ? parsed.new_user_weight_multiplier : undefined,
      newUserDaysThreshold:
        typeof parsed.new_user_days_threshold === 'number' ? parsed.new_user_days_threshold : undefined,
      showNewUserBonusBanner:
        typeof parsed.show_new_user_bonus_banner === 'boolean' ? parsed.show_new_user_bonus_banner : undefined,
      tradeQualification: tradeQualification
        ? {
            enabled: tradeQualification.enabled === true,
            eligibleMarkets: Array.isArray(tradeQualification.eligible_markets)
              ? tradeQualification.eligible_markets.filter((market: unknown) => typeof market === 'string')
              : undefined,
            minTradeValueUsdc:
              typeof tradeQualification.min_trade_value_usdc === 'number'
                ? tradeQualification.min_trade_value_usdc
                : undefined,
            requiredForDraw: tradeQualification.required_for_draw !== false,
            pendingReminderEmailsEnabled: tradeQualification.pending_reminder_emails_enabled === true,
          }
        : undefined,
      joinPreferences: joinPreferences
        ? {
            cardNetwork: joinPreferences.card_network
              ? {
                  enabled: joinPreferences.card_network.enabled === true,
                  required: joinPreferences.card_network.required !== false,
                  options: Array.isArray(joinPreferences.card_network.options)
                    ? joinPreferences.card_network.options.filter(
                        (option: unknown): option is CampaignCardPreference =>
                          option === 'visa' || option === 'unionpay'
                      )
                    : ['visa', 'unionpay'],
                }
              : undefined,
          }
        : undefined,
      legal: legal
        ? {
            termsAndConditions:
              typeof legal.terms_and_conditions === 'string'
                ? legal.terms_and_conditions.trim() || undefined
                : undefined,
            privacyPolicy:
              typeof legal.privacy_policy === 'string' ? legal.privacy_policy.trim() || undefined : undefined,
            termsRequireAcknowledgement: legal.terms_require_acknowledgement === true,
            privacyRequireAcknowledgement: legal.privacy_require_acknowledgement === true,
            requireAcknowledgement: legal.require_acknowledgement === true,
            contentFormat: legal.content_format === 'plain' ? 'plain' : 'markdown',
          }
        : undefined,
      prizeTypesByTierOrder: parsePrizeTypesByTierOrder(parsed.prize_types_by_tier_order),
    };
  } catch {
    return {};
  }
}

function parsePrizeTypesByTierOrder(value: unknown): Partial<Record<number, CampaignPrizeType>> | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const result: Partial<Record<number, CampaignPrizeType>> = {};

  Object.entries(value as Record<string, unknown>).forEach(([tierOrder, prizeType]) => {
    const order = Number(tierOrder);
    if (!Number.isFinite(order) || order <= 0) {
      return;
    }

    if (prizeType === 'digital_asset' || prizeType === 'voucher' || prizeType === 'other') {
      result[order] = prizeType;
    }
  });

  return Object.keys(result).length > 0 ? result : undefined;
}

export function requiresTradeQualification(rules: ParsedCampaignRules): boolean {
  return Boolean(rules.tradeQualification?.enabled && rules.tradeQualification.requiredForDraw !== false);
}

export function requiresCardNetworkPreference(rules: ParsedCampaignRules): boolean {
  const cardNetwork = rules.joinPreferences?.cardNetwork;
  return Boolean(cardNetwork?.enabled && cardNetwork.required !== false);
}

export function supportsCardNetworkPreference(rules: ParsedCampaignRules): boolean {
  return Boolean(rules.joinPreferences?.cardNetwork?.enabled);
}

export function hasCampaignLegalDocuments(rules: ParsedCampaignRules): boolean {
  return Boolean(rules.legal?.termsAndConditions || rules.legal?.privacyPolicy);
}

export function termsRequireAcknowledgement(rules: ParsedCampaignRules): boolean {
  const legal = rules.legal;
  if (!legal?.termsAndConditions) {
    return false;
  }

  return Boolean(legal.termsRequireAcknowledgement || legal.requireAcknowledgement);
}

export function privacyRequiresAcknowledgement(rules: ParsedCampaignRules): boolean {
  const legal = rules.legal;
  if (!legal?.privacyPolicy) {
    return false;
  }

  return Boolean(legal.privacyRequireAcknowledgement || legal.requireAcknowledgement);
}

export function requiresLegalAcknowledgement(rules: ParsedCampaignRules): boolean {
  return termsRequireAcknowledgement(rules) || privacyRequiresAcknowledgement(rules);
}

export function hasViewOnlyLegalDocuments(rules: ParsedCampaignRules): boolean {
  const legal = rules.legal;
  if (!legal) {
    return false;
  }

  const termsViewOnly = Boolean(legal.termsAndConditions && !termsRequireAcknowledgement(rules));
  const privacyViewOnly = Boolean(legal.privacyPolicy && !privacyRequiresAcknowledgement(rules));

  return termsViewOnly || privacyViewOnly;
}

export const CAMPAIGN_LEGAL_MAX_LENGTH = 50_000;

export function getCardNetworkOptions(rules: ParsedCampaignRules): CampaignCardPreference[] {
  return rules.joinPreferences?.cardNetwork?.options?.length
    ? rules.joinPreferences.cardNetwork.options
    : ['visa', 'unionpay'];
}

export function usesFlatEntryWeight(rules: ParsedCampaignRules): boolean {
  return rules.entryWeightMode === 'flat';
}

export function buildRulesConfig(form: {
  baseWeight: string;
  entryWeightMode: CampaignEntryWeightMode;
  newUserWeightMultiplier: string;
  newUserDaysThreshold: string;
  showNewUserBonusBanner: boolean;
  tradeQualificationEnabled: boolean;
  eligibleMarkets: string[];
  minTradeValueUsdc: string;
  pendingQualificationReminderEmailsEnabled?: boolean;
  cardNetworkEnabled: boolean;
  cardNetworkRequired: boolean;
  termsAndConditions?: string;
  privacyPolicy?: string;
  requireTermsAcknowledgement?: boolean;
  requirePrivacyAcknowledgement?: boolean;
  prizes?: Array<{ tierOrder: number; prizeType: CampaignPrizeType }>;
}): string {
  const baseWeight = Number(form.baseWeight);
  const config: Record<string, unknown> = {
    base_weight: Number.isFinite(baseWeight) && baseWeight > 0 ? baseWeight : 1,
    entry_weight_mode: form.entryWeightMode,
  };

  if (form.entryWeightMode === 'weighted') {
    const multiplier = Number(form.newUserWeightMultiplier);
    const daysThreshold = Number.parseInt(form.newUserDaysThreshold, 10);
    const hasMultiplier = form.newUserWeightMultiplier.trim() !== '';
    const hasDaysThreshold = form.newUserDaysThreshold.trim() !== '';

    if (hasMultiplier || hasDaysThreshold) {
      if (!Number.isFinite(multiplier) || multiplier <= 0 || !Number.isFinite(daysThreshold) || daysThreshold <= 0) {
        return '';
      }

      config.new_user_weight_multiplier = multiplier;
      config.new_user_days_threshold = daysThreshold;
      config.show_new_user_bonus_banner = form.showNewUserBonusBanner;
    }
  }

  if (form.tradeQualificationEnabled) {
    const minTradeValue = Number(form.minTradeValueUsdc);
    const eligibleMarkets = form.eligibleMarkets
      .map(market => market.trim().toLowerCase().replace('/', '_'))
      .filter(Boolean);

    if (!Number.isFinite(minTradeValue) || minTradeValue <= 0 || eligibleMarkets.length === 0) {
      return '';
    }

    config.trade_qualification = {
      enabled: true,
      eligible_markets: eligibleMarkets,
      min_trade_value_usdc: minTradeValue,
      required_for_draw: true,
      ...(form.pendingQualificationReminderEmailsEnabled ? { pending_reminder_emails_enabled: true } : {}),
    };
  }

  if (form.cardNetworkEnabled) {
    config.join_preferences = {
      card_network: {
        enabled: true,
        required: form.cardNetworkRequired,
        options: ['visa', 'unionpay'],
      },
    };
  }

  const terms = form.termsAndConditions?.trim() ?? '';
  const privacy = form.privacyPolicy?.trim() ?? '';
  const requireTermsAcknowledgement = form.requireTermsAcknowledgement === true && Boolean(terms);
  const requirePrivacyAcknowledgement = form.requirePrivacyAcknowledgement === true && Boolean(privacy);

  if (terms || privacy) {
    if (terms.length > CAMPAIGN_LEGAL_MAX_LENGTH || privacy.length > CAMPAIGN_LEGAL_MAX_LENGTH) {
      return '';
    }

    config.legal = {
      content_format: 'markdown',
      ...(terms ? { terms_and_conditions: terms } : {}),
      ...(privacy ? { privacy_policy: privacy } : {}),
      ...(requireTermsAcknowledgement ? { terms_require_acknowledgement: true } : {}),
      ...(requirePrivacyAcknowledgement ? { privacy_require_acknowledgement: true } : {}),
    };
  }

  if (form.prizes?.length) {
    config.prize_types_by_tier_order = Object.fromEntries(
      form.prizes.map(prize => [String(prize.tierOrder), prize.prizeType])
    );
  }

  return JSON.stringify(config);
}

export function formatMarketList(markets: string[] | undefined): string {
  if (!markets?.length) {
    return '';
  }

  return markets.map(market => market.replace(/_/g, '/').toUpperCase()).join(', ');
}

export const COREPASS_TRADING_COMPETITION_TEMPLATE = {
  slug: 'corepass-ping-trading-competition',
  title: 'CorePass × Ping Exchange Trading Competition',
  description:
    'Complete one eligible trade on Ping Exchange for your chance to become one of 23 winners and share in a total prize pool valued at $8,447.',
  rulesConfig: JSON.stringify({
    base_weight: 1,
    entry_weight_mode: 'flat',
    trade_qualification: {
      enabled: true,
      eligible_markets: ['xcb_usdc', 'ctn_usdc'],
      min_trade_value_usdc: 25,
      required_for_draw: true,
    },
    join_preferences: {
      card_network: {
        enabled: true,
        required: true,
        options: ['visa', 'unionpay'],
      },
    },
    prize_types_by_tier_order: {
      '1': 'digital_asset',
      '2': 'digital_asset',
      '3': 'voucher',
      '4': 'digital_asset',
      '5': 'voucher',
      '6': 'voucher',
      '7': 'digital_asset',
    },
  }),
  prizes: [
    { tierName: '1st Prize', amount: '2000', currency: 'USDC', quantity: '1', prizeType: 'digital_asset' as const },
    { tierName: '2nd Prize', amount: '1000', currency: 'USDC', quantity: '1', prizeType: 'digital_asset' as const },
    {
      tierName: '654 Club Bank Card',
      amount: '654',
      currency: 'USD',
      quantity: '3',
      prizeType: 'voucher' as const,
    },
    { tierName: '3rd Prize', amount: '500', currency: 'USDC', quantity: '2', prizeType: 'digital_asset' as const },
    {
      tierName: 'Fortis Bank Card',
      amount: '310',
      currency: 'USD',
      quantity: '3',
      prizeType: 'voucher' as const,
    },
    {
      tierName: 'Structus Bank Card',
      amount: '185',
      currency: 'USD',
      quantity: '3',
      prizeType: 'voucher' as const,
    },
    { tierName: '4th Prize', amount: '100', currency: 'USDC', quantity: '10', prizeType: 'digital_asset' as const },
  ],
};
